<template>
  <div class="d-flex align-center w-100">
    <div
      v-for="main in sortedMainMenus"
      :key="main.id"
      class="menu-item"
    >
      <!-- ===================================================== -->
      <!-- 1. STANDARD NESTED DROPDOWN -->
      <!-- ===================================================== -->
      <!--
        hasDropDown=true takes precedence.

        Even if isLink=true, this MainMenu item acts only
        as a dropdown activator and cannot navigate.
      -->
      <v-menu
        v-if="toBoolean(main.hasDropDown)"
        v-model="openMenus[main.id]"
        open-on-hover
        offset="4"
        :open-delay="120"
        :close-delay="250"
        :close-on-content-click="false"
      >
        <!-- =================================================== -->
        <!-- MAIN MENU ACTIVATOR -->
        <!-- =================================================== -->
        <template #activator="{ props }">
          <v-btn
            v-bind="props"
            variant="text"
            class="menu-btn text-none"
            :class="{
              'menu-active': openMenus[main.id]
            }"
            :style="{
              color: theme.appBarColor,
              fontSize: '16px'
            }"
          >
            <!-- PREPEND ICON -->
            <v-icon
              v-if="
                toBoolean(main.showIcon) &&
                main.mdiIcon
              "
              start
              :icon="main.mdiIcon"
              size="20"
            />

            {{
              getLocalizedText(
                main,
                "desc",
                "descFR"
              )
            }}

            <v-icon
              end
              size="20"
            >
              mdi-menu-down
            </v-icon>
          </v-btn>
        </template>

        <!-- =================================================== -->
        <!-- FIRST-LEVEL COLUMNS -->
        <!-- =================================================== -->
        <v-list
          density="compact"
          min-width="280"
          :style="{
            background: theme.appBarBg,
            color: theme.appBarColor
          }"
        >
          <template
            v-for="col in getColumnsForMain(main.id)"
            :key="col.colId"
          >
            <!-- =============================================== -->
            <!-- COLUMN WITH SUBMENU -->
            <!-- =============================================== -->
            <v-menu
              v-if="
                toBoolean(col.hasDropDown) &&
                getItemsForColumn(col.colId).length > 0
              "
              open-on-hover
              location="end"
              :open-delay="100"
              :close-delay="200"
              :close-on-content-click="true"
            >
              <template #activator="{ props: columnProps }">
                <v-list-item
                  v-bind="columnProps"
                  class="nested-activator"
                >
                  <template #prepend>
                    <v-icon
                      v-if="col.mdiIcon"
                      :icon="col.mdiIcon"
                      size="24"
                    />
                  </template>

                  <v-list-item-title>
                    {{
                      getLocalizedText(
                        col,
                        "desc",
                        "descFR"
                      )
                    }}
                  </v-list-item-title>

                  <template #append>
                    <v-icon size="small">
                      mdi-menu-right
                    </v-icon>
                  </template>
                </v-list-item>
              </template>

              <!-- ============================================= -->
              <!-- COLUMN ITEMS -->
              <!-- ============================================= -->
              <v-list
                density="compact"
                min-width="320"
                :style="{
                  background: theme.appBarBg,
                  color: theme.appBarColor
                }"
              >
                <template
                  v-for="item in getItemsForColumn(col.colId)"
                  :key="item.id"
                >
                  <!-- CLICKABLE ITEM -->
                  <component
                    :is="
                      getMenuHref(item)
                        ? 'a'
                        : 'router-link'
                    "
                    v-if="isMenuItemClickable(item)"
                    :to="getMenuRoute(item)"
                    :href="getMenuHref(item)"
                    :target="
                      getMenuHref(item)
                        ? '_blank'
                        : undefined
                    "
                    :rel="
                      getMenuHref(item)
                        ? 'noopener noreferrer'
                        : undefined
                    "
                    class="
                      nested-item-link
                      no-link-style
                    "
                    @click="closeMainMenu(main.id)"
                  >
                    <v-list-item>
                      <template #prepend>
                        <v-icon
                          v-if="item.mdiIcon"
                          :icon="item.mdiIcon"
                          size="24"
                        />
                      </template>

                      <v-list-item-title>
                        {{
                          getLocalizedText(
                            item,
                            "mainDesc",
                            "mainDescFR"
                          )
                        }}
                      </v-list-item-title>

                      <v-list-item-subtitle>
                        {{
                          getLocalizedText(
                            item,
                            "subDesc",
                            "subDescFR"
                          )
                        }}
                      </v-list-item-subtitle>
                    </v-list-item>
                  </component>

                  <!-- NON-CLICKABLE ITEM -->
                  <v-list-item
                    v-else
                    disabled
                  >
                    <template #prepend>
                      <v-icon
                        v-if="item.mdiIcon"
                        :icon="item.mdiIcon"
                        size="24"
                      />
                    </template>

                    <v-list-item-title>
                      {{
                        getLocalizedText(
                          item,
                          "mainDesc",
                          "mainDescFR"
                        )
                      }}
                    </v-list-item-title>

                    <v-list-item-subtitle>
                      {{
                        getLocalizedText(
                          item,
                          "subDesc",
                          "subDescFR"
                        )
                      }}
                    </v-list-item-subtitle>
                  </v-list-item>
                </template>
              </v-list>
            </v-menu>

            <!-- =============================================== -->
            <!-- COLUMN WITHOUT SUBMENU -->
            <!-- =============================================== -->
            <v-list-item
              v-else
              class="nested-activator"
            >
              <template #prepend>
                <v-icon
                  v-if="col.mdiIcon"
                  :icon="col.mdiIcon"
                  size="24"
                />
              </template>

              <v-list-item-title>
                {{
                  getLocalizedText(
                    col,
                    "desc",
                    "descFR"
                  )
                }}
              </v-list-item-title>
            </v-list-item>
          </template>

          <!-- EMPTY -->
          <v-list-item
            v-if="
              getColumnsForMain(main.id).length === 0
            "
            disabled
          >
            <v-list-item-title>
              {{
                currentLanguage === "fr"
                  ? "Aucun élément disponible"
                  : "No items available"
              }}
            </v-list-item-title>
          </v-list-item>
        </v-list>
      </v-menu>

      <!-- ===================================================== -->
      <!-- 2. DIRECT MAIN MENU LINK -->
      <!-- ===================================================== -->
      <!--
        Navigatable ONLY when:
          hasDropDown = false
          isLink      = true
          valid destination exists
      -->
      <component
        :is="
          getMenuHref(main)
            ? 'a'
            : 'router-link'
        "
        v-else-if="isMainMenuNavigatable(main)"
        :to="getMenuRoute(main)"
        :href="getMenuHref(main)"
        :target="
          getMenuHref(main)
            ? '_blank'
            : undefined
        "
        :rel="
          getMenuHref(main)
            ? 'noopener noreferrer'
            : undefined
        "
        class="
          main-menu-link
          no-link-style
        "
        :style="{
          color: theme.appBarColor
        }"
      >
        <v-btn
          variant="text"
          class="menu-btn text-none"
          :style="{
            color: theme.appBarColor,
            fontSize: '16px'
          }"
        >
          <!-- PREPEND ICON -->
          <v-icon
            v-if="
              toBoolean(main.showIcon) &&
              main.mdiIcon
            "
            start
            :icon="main.mdiIcon"
            size="20"
          />

          {{
            getLocalizedText(
              main,
              "desc",
              "descFR"
            )
          }}
        </v-btn>
      </component>

      <!-- ===================================================== -->
      <!-- 3. FULL-WIDTH MEGA MENU -->
      <!-- ===================================================== -->
      <!--
        Reached when:

          hasDropDown = false
          isLink      = false

        This preserves the existing full-width mega-menu mode.
      -->
      <v-menu
        v-else
        v-model="openMenus[main.id]"
        open-on-hover
        offset="4"
        eager
        :open-delay="150"
        :close-delay="300"
        transition="scale-transition"
        content-class="mega-menu"
        width="100%"
        :close-on-content-click="true"
      >
        <!-- =================================================== -->
        <!-- ACTIVATOR -->
        <!-- =================================================== -->
        <template #activator="{ props }">
          <v-btn
            v-bind="props"
            variant="text"
            class="menu-btn text-none"
            :class="{
              'menu-active': openMenus[main.id]
            }"
            :style="{
              color: theme.appBarColor,
              fontSize: '16px'
            }"
          >
            <!-- PREPEND ICON -->
            <v-icon
              v-if="
                toBoolean(main.showIcon) &&
                main.mdiIcon
              "
              start
              :icon="main.mdiIcon"
              size="20"
            />

            {{
              getLocalizedText(
                main,
                "desc",
                "descFR"
              )
            }}

            <v-icon
              end
              size="20"
            >
              mdi-menu-down
            </v-icon>
          </v-btn>
        </template>

        <!-- =================================================== -->
        <!-- FULL-WIDTH MEGA MENU CONTENT -->
        <!-- =================================================== -->
        <v-container
          fluid
          class="pa-0 mega-menu-container"
          :style="{
            background: theme.appBarBg,
            color: theme.appBarColor
          }"
        >
          <v-row
            no-gutters
            class="mega-menu-row"
          >
            <v-col
              v-for="col in getColumnsForMain(main.id)"
              :key="col.colId"
              cols="12"
              sm="6"
              md="4"
              lg="3"
              class="mega-column"
            >
              <!-- ============================================= -->
              <!-- COLUMN IMAGE -->
              <!-- ============================================= -->
              <div
                v-if="
                  toBoolean(col.showColImage) &&
                  col.colImage
                "
                class="column-image-wrapper"
              >
                <img
                  :src="col.colImage"
                  :alt="
                    getLocalizedText(
                      col,
                      'desc',
                      'descFR'
                    )
                  "
                  class="column-image"
                />
              </div>

              <!-- ============================================= -->
              <!-- NORMAL COLUMN -->
              <!-- ============================================= -->
              <div v-else>
                <div class="column-heading-row">
                  <v-icon
                    v-if="col.mdiIcon"
                    :icon="col.mdiIcon"
                    size="24"
                  />

                  <h4 class="column-heading">
                    {{
                      getLocalizedText(
                        col,
                        "desc",
                        "descFR"
                      )
                    }}
                  </h4>
                </div>

                <!-- =========================================== -->
                <!-- ITEMS -->
                <!-- =========================================== -->
                <template
                  v-for="item in getItemsForColumn(col.colId)"
                  :key="item.id"
                >
                  <!-- CLICKABLE ITEM -->
                  <component
                    :is="
                      getMenuHref(item)
                        ? 'a'
                        : 'router-link'
                    "
                    v-if="isMenuItemClickable(item)"
                    :to="getMenuRoute(item)"
                    :href="getMenuHref(item)"
                    :target="
                      getMenuHref(item)
                        ? '_blank'
                        : undefined
                    "
                    :rel="
                      getMenuHref(item)
                        ? 'noopener noreferrer'
                        : undefined
                    "
                    class="
                      menu-link
                      no-link-style
                    "
                    :style="{
                      color: theme.appBarColor
                    }"
                    @click="closeMainMenu(main.id)"
                  >
                    <v-icon
                      v-if="item.mdiIcon"
                      :icon="item.mdiIcon"
                      size="24"
                      class="menu-link-icon"
                    />

                    <div class="menu-link-content">
                      <div class="menu-main-description">
                        {{
                          getLocalizedText(
                            item,
                            "mainDesc",
                            "mainDescFR"
                          )
                        }}
                      </div>

                      <div
                        v-if="
                          getLocalizedText(
                            item,
                            'subDesc',
                            'subDescFR'
                          )
                        "
                        class="menu-sub-description"
                      >
                        {{
                          getLocalizedText(
                            item,
                            "subDesc",
                            "subDescFR"
                          )
                        }}
                      </div>
                    </div>
                  </component>

                  <!-- NON-CLICKABLE ITEM -->
                  <div
                    v-else
                    class="
                      menu-link
                      disabled-menu-link
                    "
                    :style="{
                      color: theme.appBarColor
                    }"
                  >
                    <v-icon
                      v-if="item.mdiIcon"
                      :icon="item.mdiIcon"
                      size="24"
                      class="menu-link-icon"
                    />

                    <div class="menu-link-content">
                      <div class="menu-main-description">
                        {{
                          getLocalizedText(
                            item,
                            "mainDesc",
                            "mainDescFR"
                          )
                        }}
                      </div>

                      <div
                        v-if="
                          getLocalizedText(
                            item,
                            'subDesc',
                            'subDescFR'
                          )
                        "
                        class="menu-sub-description"
                      >
                        {{
                          getLocalizedText(
                            item,
                            "subDesc",
                            "subDescFR"
                          )
                        }}
                      </div>
                    </div>
                  </div>
                </template>

                <!-- EMPTY COLUMN -->
                <div
                  v-if="
                    getItemsForColumn(
                      col.colId
                    ).length === 0
                  "
                  class="empty-column"
                >
                  {{
                    currentLanguage === "fr"
                      ? "Aucun élément disponible"
                      : "No items available"
                  }}
                </div>
              </div>
            </v-col>

            <!-- ================================================= -->
            <!-- NO COLUMNS -->
            <!-- ================================================= -->
            <v-col
              v-if="
                getColumnsForMain(
                  main.id
                ).length === 0
              "
              cols="12"
            >
              <div class="empty-main-menu">
                {{
                  currentLanguage === "fr"
                    ? "Aucun contenu disponible"
                    : "No menu content available"
                }}
              </div>
            </v-col>
          </v-row>
        </v-container>
      </v-menu>
    </div>
  </div>

  <!-- ======================================================= -->
  <!-- ERROR -->
  <!-- ======================================================= -->

  <v-snackbar
    :model-value="Boolean(loadError)"
    color="error"
    timeout="8000"
  >
    {{ loadError }}
  </v-snackbar>
</template>

<script setup>
import {
  computed,
  onMounted,
  ref,
  watch
} from "vue";

import {
  useI18n
} from "vue-i18n";

import publicApi
  from "@/auth/publicApi";

import {
  useLangStore
} from "@/store/lang";

import {
  useThemeStore
} from "@/store/auth";

// ============================================================
// STORES
// ============================================================

const lang =
  useLangStore();

const themeStore =
  useThemeStore();

const {
  locale
} = useI18n();

// ============================================================
// DATA
// ============================================================

const megaMainMenu =
  ref([]);

const megaColumn =
  ref([]);

const megaColumnItem =
  ref([]);

const megaColumnImage =
  ref([]);

// ============================================================
// STATE
// ============================================================

const loading =
  ref(false);

const loadError =
  ref("");

const openMenus =
  ref({});

// ============================================================
// LANGUAGE
// ============================================================

const currentLanguage =
  computed(
    () =>
      lang.currentLanguage ||
      "en"
  );

watch(
  currentLanguage,
  newLanguage => {
    locale.value =
      newLanguage ||
      "en";
  },
  {
    immediate: true
  }
);

// ============================================================
// THEME
// ============================================================

const theme =
  computed(
    () =>
      themeStore.themeStyles ||
      {
        appBarBg:
          "#ffffff",

        appBarColor:
          "#000000"
      }
  );

// ============================================================
// NORMALIZE ARRAY
// ============================================================

function normalizeArray(
  data
) {
  if (
    Array.isArray(data)
  ) {
    return data;
  }

  if (
    Array.isArray(
      data?.data
    )
  ) {
    return data.data;
  }

  if (
    Array.isArray(
      data?.items
    )
  ) {
    return data.items;
  }

  if (
    Array.isArray(
      data?.results
    )
  ) {
    return data.results;
  }

  if (
    Array.isArray(
      data?.value
    )
  ) {
    return data.value;
  }

  return [];
}

// ============================================================
// BOOLEAN NORMALIZATION
// ============================================================

function toBoolean(
  value
) {
  if (
    typeof value ===
    "boolean"
  ) {
    return value;
  }

  if (
    typeof value ===
    "number"
  ) {
    return value ===
      1;
  }

  if (
    typeof value ===
    "string"
  ) {
    return [
      "true",
      "1",
      "yes",
      "on"
    ].includes(
      value
        .trim()
        .toLowerCase()
    );
  }

  return false;
}

// ============================================================
// ID COMPARISON
// ============================================================

function sameId(
  firstId,
  secondId
) {
  if (
    firstId == null ||
    secondId == null
  ) {
    return false;
  }

  return (
    String(firstId)
      .trim() ===
    String(secondId)
      .trim()
  );
}

// ============================================================
// MAIN MENU SORTING
// ============================================================

const sortedMainMenus =
  computed(
    () =>
      [...megaMainMenu.value]
        .sort(
          (
            first,
            second
          ) =>
            Number(
              first?.colOrder ||
              0
            ) -
            Number(
              second?.colOrder ||
              0
            )
        )
  );

// ============================================================
// MAIN MENU COLUMNS
// ============================================================

function getColumnsForMain(
  mainMenuId
) {
  return megaColumn.value
    .filter(
      column =>
        sameId(
          column.mainMenuId,
          mainMenuId
        )
    )
    .sort(
      (
        first,
        second
      ) =>
        Number(
          first?.colOrder ||
          0
        ) -
        Number(
          second?.colOrder ||
          0
        )
    );
}

// ============================================================
// COLUMN ITEMS
// ============================================================

function getItemsForColumn(
  columnId
) {
  return megaColumnItem.value
    .filter(
      item =>
        sameId(
          item.colId,
          columnId
        ) &&
        toBoolean(
          item.isVisible ??
          true
        )
    )
    .sort(
      (
        first,
        second
      ) =>
        Number(
          first?.menuorder ||
          0
        ) -
        Number(
          second?.menuorder ||
          0
        )
    );
}

// ============================================================
// LOCALIZATION
// ============================================================

function getLocalizedText(
  record,
  englishProperty,
  frenchProperty
) {
  if (!record) {
    return "";
  }

  if (
    currentLanguage.value ===
    "fr"
  ) {
    return (
      record[frenchProperty] ||
      record[englishProperty] ||
      ""
    );
  }

  return (
    record[englishProperty] ||
    record[frenchProperty] ||
    ""
  );
}

// ============================================================
// CLEAN STRING VALUE
// ============================================================

function cleanValue(
  value
) {
  const normalized =
    String(
      value ??
      ""
    ).trim();

  if (
    !normalized ||
    normalized === "#" ||
    normalized
      .toLowerCase() ===
      "null" ||
    normalized
      .toLowerCase() ===
      "undefined"
  ) {
    return "";
  }

  return normalized;
}

// ============================================================
// EXTERNAL URL
// ============================================================

function isExternalUrl(
  value
) {
  return /^(https?:)?\/\//i
    .test(
      cleanValue(
        value
      )
    );
}

// ============================================================
// DYNAMIC PAGE SLUG
// ============================================================

function getDynamicPageSlug(
  item
) {
  if (
    currentLanguage.value ===
    "fr"
  ) {
    return cleanValue(
      item?.dynamicPageSlugFr ||
      item?.dynamicPageSlugEn
    );
  }

  return cleanValue(
    item?.dynamicPageSlugEn ||
    item?.dynamicPageSlugFr
  );
}

// ============================================================
// STATIC ROUTE
// ============================================================

function getStaticRouteValue(
  item
) {
  if (
    currentLanguage.value ===
    "fr"
  ) {
    return cleanValue(
      item?.menuPathFr ||
      item?.menuPathEn ||
      item?.linkPathFr ||
      item?.linkPath
    );
  }

  return cleanValue(
    item?.menuPathEn ||
    item?.menuPathFr ||
    item?.linkPath ||
    item?.linkPathFr
  );
}

// ============================================================
// EXTERNAL LINK
// ============================================================

function getExternalLink(
  item
) {
  if (
    currentLanguage.value ===
    "fr"
  ) {
    return cleanValue(
      item?.linkPathFr ||
      item?.linkPath
    );
  }

  return cleanValue(
    item?.linkPath ||
    item?.linkPathFr
  );
}

// ============================================================
// MENU ITEM TYPE
// ============================================================

function normalizeMenuItemType(
  item
) {
  return cleanValue(
    item?.menuItemType
  ).toLowerCase();
}

// ============================================================
// LANGUAGE-AWARE PATH
// ============================================================

function withLanguagePrefix(
  value
) {
  const cleaned =
    cleanValue(
      value
    );

  if (!cleaned) {
    return undefined;
  }

  /*
   * If the database contains an absolute internal
   * path already, respect it exactly.
   */
  if (
    cleaned.startsWith("/")
  ) {
    return {
      path:
        cleaned
    };
  }

  /*
   * Otherwise treat it as a localized public path.
   */
  return {
    path:
      `/${currentLanguage.value}/${cleaned.replace(/^\/+/, "")}`
  };
}

// ============================================================
// ROUTE RESOLUTION
// ============================================================

function getMenuRoute(
  item
) {
  if (!item) {
    return undefined;
  }

  const itemType =
    normalizeMenuItemType(
      item
    );

  switch (
    itemType
  ) {
    // ========================================================
    // DYNAMIC PAGE
    // ========================================================

    case "dynamicpage":
    case "dynamic": {
      if (
        !item.dynamicPageId ||
        item.dynamicPageIsPublished ===
          false
      ) {
        return undefined;
      }

      const slug =
        getDynamicPageSlug(
          item
        );

      if (!slug) {
        return undefined;
      }

      return {
        name:
          "DynamicPublicPage",

        params: {
          lang:
            currentLanguage.value,

          pagePath:
            slug
              .split("/")
              .map(
                segment =>
                  segment.trim()
              )
              .filter(
                Boolean
              )
        }
      };
    }

    // ========================================================
    // STATIC ROUTE
    // ========================================================

    case "static":
    case "staticroute": {
      const routeValue =
        getStaticRouteValue(
          item
        );

      if (
        !routeValue ||
        isExternalUrl(
          routeValue
        )
      ) {
        return undefined;
      }

      return withLanguagePrefix(
        routeValue
      );
    }

    // ========================================================
    // PARAMETERIZED ROUTE
    // ========================================================

    case "parameterizedroute": {
      const routeValue =
        getStaticRouteValue(
          item
        );

      if (
        !routeValue ||
        isExternalUrl(
          routeValue
        )
      ) {
        return undefined;
      }

      return withLanguagePrefix(
        routeValue
      );
    }

    // ========================================================
    // NON-NAVIGATING
    // ========================================================

    case "heading":
    case "none":
      return undefined;

    // ========================================================
    // BACKWARD COMPATIBILITY
    // ========================================================

    default: {
      /*
       * Existing records may not yet have MenuItemType.
       */
      if (
        item.dynamicPageId
      ) {
        if (
          item.dynamicPageIsPublished ===
          false
        ) {
          return undefined;
        }

        const slug =
          getDynamicPageSlug(
            item
          );

        if (!slug) {
          return undefined;
        }

        return {
          name:
            "DynamicPublicPage",

          params: {
            lang:
              currentLanguage.value,

            pagePath:
              slug
                .split("/")
                .map(
                  segment =>
                    segment.trim()
                )
                .filter(
                  Boolean
                )
          }
        };
      }

      const routeValue =
        getStaticRouteValue(
          item
        );

      if (
        !routeValue ||
        isExternalUrl(
          routeValue
        )
      ) {
        return undefined;
      }

      /*
       * Named route support:
       *
       * menuPathEn / menuPathFr are frequently Vue
       * route names rather than URLs.
       */
      const explicitMenuPath =
        currentLanguage.value ===
        "fr"
          ? cleanValue(
              item?.menuPathFr ||
              item?.menuPathEn
            )
          : cleanValue(
              item?.menuPathEn ||
              item?.menuPathFr
            );

      if (
        explicitMenuPath &&
        !explicitMenuPath.startsWith("/")
      ) {
        return {
          name:
            explicitMenuPath
        };
      }

      return withLanguagePrefix(
        routeValue
      );
    }
  }
}

// ============================================================
// EXTERNAL HREF
// ============================================================

function getMenuHref(
  item
) {
  if (
    !item ||
    item.dynamicPageId
  ) {
    return undefined;
  }

  const link =
    getExternalLink(
      item
    );

  return isExternalUrl(
    link
  )
    ? link
    : undefined;
}

// ============================================================
// COLUMN ITEM CLICKABILITY
// ============================================================

function isMenuItemClickable(
  item
) {
  if (!item) {
    return false;
  }

  /*
   * Respect IsLink when present.
   *
   * Older records without IsLink continue working.
   */
  if (
    item.isLink !==
      undefined &&
    item.isLink !==
      null &&
    !toBoolean(
      item.isLink
    )
  ) {
    return false;
  }

  return Boolean(
    getMenuRoute(
      item
    ) ||
    getMenuHref(
      item
    )
  );
}

// ============================================================
// MAIN MENU DIRECT LINK RULE
// ============================================================

function isMainMenuNavigatable(
  main
) {
  if (!main) {
    return false;
  }

  /*
   * RULE 1:
   *
   * Dropdown MainMenu records can NEVER navigate
   * directly from the top-level button.
   */
  if (
    toBoolean(
      main.hasDropDown
    )
  ) {
    return false;
  }

  /*
   * RULE 2:
   *
   * It must explicitly be configured as a link.
   */
  if (
    !toBoolean(
      main.isLink
    )
  ) {
    return false;
  }

  /*
   * RULE 3:
   *
   * A real destination must exist.
   */
  return Boolean(
    getMenuRoute(
      main
    ) ||
    getMenuHref(
      main
    )
  );
}

// ============================================================
// CLOSE MAIN MENU
// ============================================================

function closeMainMenu(
  mainMenuId
) {
  if (
    mainMenuId == null
  ) {
    return;
  }

  openMenus.value[
    mainMenuId
  ] = false;
}

// ============================================================
// LOAD DATA
// ============================================================

async function loadMegaMenu() {
  loading.value =
    true;

  loadError.value =
    "";

  try {
    const [
      mainMenuResponse,
      columnResponse,
      itemResponse,
      imageResponse
    ] =
      await Promise.all([
        publicApi.get(
          "/MainMenu"
        ),

        publicApi.get(
          "/MegamenuColumn"
        ),

        publicApi.get(
          "/MegamenuItems"
        ),

        publicApi.get(
          "/MegamenuImages"
        )
      ]);

    megaMainMenu.value =
      normalizeArray(
        mainMenuResponse.data
      );

    megaColumn.value =
      normalizeArray(
        columnResponse.data
      );

    megaColumnItem.value =
      normalizeArray(
        itemResponse.data
      );

    megaColumnImage.value =
      normalizeArray(
        imageResponse.data
      );
  }
  catch (error) {
    console.error(
      "Unable to load mega menu:",
      error
    );

    loadError.value =
      error?.response
        ?.data
        ?.message ||
      error?.response
        ?.data
        ?.detail ||
      error?.message ||
      "Unable to load the menu.";
  }
  finally {
    loading.value =
      false;
  }
}

// ============================================================
// INIT
// ============================================================

onMounted(
  loadMegaMenu
);
</script>

<style scoped>
/* ============================================================
   TOP-LEVEL MENU
============================================================ */

.menu-item {
  display: flex;
  align-items: center;
  margin-right: 4px;
}

.menu-btn {
  position: relative;
  min-width: 0;
  overflow: visible;
  font-weight: 400;
}

.menu-btn
:deep(.v-btn__content) {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

/* ============================================================
   TOP LEVEL ICON
============================================================ */

.menu-btn
:deep(.v-icon--start) {
  margin-inline-end: 7px;
}

/* ============================================================
   TOP-LEVEL DIRECT LINK
============================================================ */

.main-menu-link {
  display: inline-flex;
  align-items: center;
  color: inherit;
  text-decoration: none;
}

.main-menu-link:hover {
  color: inherit;
  text-decoration: none;
}

/* ============================================================
   TOP-LEVEL HOVER / ACTIVE
============================================================ */

.menu-btn:hover,
.menu-active {
  border-bottom:
    2px solid red;
}

/*
 * Dropdown activators get the visual pointer triangle.
 */
.menu-active::before {
  content: "";

  position: absolute;

  left: 50%;
  bottom: -18px;

  transform:
    translateX(-50%);

  width: 0;
  height: 0;

  border-left:
    16px solid transparent;

  border-right:
    16px solid transparent;

  border-bottom:
    16px solid red;
}

/* ============================================================
   NESTED DROPDOWN
============================================================ */

.nested-activator {
  min-height: 54px;
}

.nested-activator:hover {
  background-color:
    rgba(
      0,
      0,
      0,
      0.04
    );

  cursor: pointer;
}

.nested-item-link {
  display: block;
  color: inherit;
  text-decoration: none;
}

/* ============================================================
   FULL MEGA MENU
============================================================ */

.mega-menu {
  min-width:
    min(
      1100px,
      95vw
    );

  max-width:
    100vw;

  border-radius:
    12px;

  will-change:
    transform,
    opacity;

  transform:
    translateZ(0);
}

.mega-menu-container {
  margin-top: 16px;

  border-radius: 8px;

  overflow: hidden;
}

.mega-menu-row {
  padding:
    30px
    50px
    50px;
}

.mega-column {
  min-height:
    200px;

  padding:
    20px;
}

/* ============================================================
   COLUMN HEADING
============================================================ */

.column-heading-row {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 16px;
}

.column-heading {
  margin: 0;

  font-size: 18px;

  line-height: 1.3;
}

/* ============================================================
   COLUMN IMAGE
============================================================ */

.column-image-wrapper {
  width: 100%;
}

.column-image {
  display: block;

  width: 80%;
  max-width: 100%;

  height: auto;

  object-fit: cover;

  border-radius: 8px;
}

/* ============================================================
   MEGA MENU ITEM
============================================================ */

.menu-link {
  display: flex;
  align-items: flex-start;

  gap: 12px;

  padding:
    10px
    6px;

  margin-bottom: 6px;

  border-radius: 6px;

  transition:
    background-color
      0.2s ease,
    transform
      0.2s ease;
}

.menu-link:hover {
  background:
    rgba(
      201,
      166,
      107,
      0.14
    );

  transform:
    translateX(
      3px
    );
}

.menu-link-icon {
  flex:
    0 0 auto;

  margin-top:
    2px;
}

.menu-link-content {
  min-width:
    0;
}

.menu-main-description {
  font-size:
    16px;

  line-height:
    1.35;
}

.menu-sub-description {
  margin-top:
    3px;

  font-size:
    12px;

  line-height:
    1.4;

  opacity:
    0.75;
}

/* ============================================================
   LINKS
============================================================ */

.no-link-style {
  color: inherit;

  text-decoration: none;
}

/* ============================================================
   DISABLED ITEM
============================================================ */

.disabled-menu-link {
  cursor: default;

  opacity: 0.65;
}

.disabled-menu-link:hover {
  background: transparent;

  transform: none;
}

/* ============================================================
   EMPTY STATES
============================================================ */

.empty-column {
  padding:
    8px
    0;

  font-size:
    13px;

  opacity:
    0.65;
}

.empty-main-menu {
  width: 100%;

  padding: 35px;

  text-align: center;

  font-size: 13px;

  opacity: 0.65;
}

/* ============================================================
   RESPONSIVE
============================================================ */

@media (
  max-width: 960px
) {
  .mega-menu-row {
    padding: 20px;
  }

  .mega-menu {
    min-width: 90vw;
  }
}
</style>